import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  Download,
  Print,
  Refresh,
  Search as SearchIcon,
  CreditCard as CreditCardIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  PictureAsPdf,
  Description,
  Clear as ClearIcon,
} from '@mui/icons-material';

const EMIReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    searchTerm: '',
  });

  const sampleData = [
    {
      sno: 1,
      bookingId: 'BK001',
      customerName: 'Amit Gupta',
      plotNo: 'P101',
      emiNo: 1,
      dueDate: '2025-01-01',
      emiAmount: 40000,
      paidAmount: 40000,
      paidDate: '2025-01-01',
      status: 'Paid',
    },
    {
      sno: 2,
      bookingId: 'BK001',
      customerName: 'Amit Gupta',
      plotNo: 'P101',
      emiNo: 2,
      dueDate: '2025-02-01',
      emiAmount: 40000,
      paidAmount: 0,
      paidDate: '-',
      status: 'Pending',
    },
    {
      sno: 3,
      bookingId: 'BK003',
      customerName: 'Mike Johnson',
      plotNo: 'P103',
      emiNo: 1,
      dueDate: '2025-01-15',
      emiAmount: 50000,
      paidAmount: 50000,
      paidDate: '2025-01-15',
      status: 'Paid',
    },
    {
      sno: 4,
      bookingId: 'BK003',
      customerName: 'Mike Johnson',
      plotNo: 'P103',
      emiNo: 2,
      dueDate: '2025-02-15',
      emiAmount: 50000,
      paidAmount: 0,
      paidDate: '-',
      status: 'Overdue',
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export functionality
  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (type) => {
    if (type === 'csv') {
      handleExportCsv();
    } else if (type === 'pdf') {
      handleExportPdf();
    }
    handleExportClose();
  };

  const handleExportCsv = () => {
    const filteredData = getFilteredData();
    const headers = ["S.No", "Booking ID", "Customer Name", "Plot No", "EMI No", "Due Date", "EMI Amount", "Paid Amount", "Paid Date", "Status"];
    const rows = filteredData.map(emi => [
      emi.sno,
      emi.bookingId,
      emi.customerName,
      emi.plotNo,
      emi.emiNo,
      emi.dueDate,
      `₹${emi.emiAmount.toLocaleString('en-IN')}`,
      `₹${emi.paidAmount.toLocaleString('en-IN')}`,
      emi.paidDate,
      emi.status
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `EMI_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPdf = () => {
    const filteredData = getFilteredData();
    const headers = ["S.No", "Booking ID", "Customer Name", "Plot No", "EMI No", "Due Date", "EMI Amount", "Paid Amount", "Paid Date", "Status"];
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>EMI Report</title>');
    printWindow.document.write('<style>');
    printWindow.document.write(`
      body { font-family: Arial, sans-serif; margin: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
      th { background-color: #f2f2f2; font-weight: bold; }
      .header { text-align: center; margin-bottom: 20px; }
      .summary { margin-bottom: 20px; }
      .summary-item { display: inline-block; margin-right: 20px; padding: 10px; background-color: #f9f9f9; border-radius: 5px; }
    `);
    printWindow.document.write('</style></head><body>');
    printWindow.document.write('<div class="header"><h1>EMI Report</h1>');
    printWindow.document.write(`<p>Generated on: ${new Date().toLocaleDateString()}</p></div>`);

    // Summary
    const totalEMI = filteredData.reduce((sum, item) => sum + item.emiAmount, 0);
    const totalPaid = filteredData.reduce((sum, item) => sum + item.paidAmount, 0);
    const totalDue = totalEMI - totalPaid;
    const paidEMIs = filteredData.filter(item => item.status === 'Paid').length;
    const pendingEMIs = filteredData.filter(item => item.status === 'Pending').length;
    const overdueEMIs = filteredData.filter(item => item.status === 'Overdue').length;

    printWindow.document.write('<div class="summary">');
    printWindow.document.write(`<div class="summary-item">Total EMIs: ${filteredData.length}</div>`);
    printWindow.document.write(`<div class="summary-item">Paid EMIs: ${paidEMIs}</div>`);
    printWindow.document.write(`<div class="summary-item">Pending EMIs: ${pendingEMIs}</div>`);
    printWindow.document.write(`<div class="summary-item">Overdue EMIs: ${overdueEMIs}</div>`);
    printWindow.document.write(`<div class="summary-item">Total Amount: ₹${totalEMI.toLocaleString('en-IN')}</div>`);
    printWindow.document.write(`<div class="summary-item">Paid Amount: ₹${totalPaid.toLocaleString('en-IN')}</div>`);
    printWindow.document.write(`<div class="summary-item">Due Amount: ₹${totalDue.toLocaleString('en-IN')}</div>`);
    printWindow.document.write('</div>');

    // Table
    printWindow.document.write('<table>');
    printWindow.document.write('<thead><tr>');
    headers.forEach(header => {
      printWindow.document.write(`<th>${header}</th>`);
    });
    printWindow.document.write('</tr></thead><tbody>');

    filteredData.forEach(emi => {
      printWindow.document.write('<tr>');
      printWindow.document.write(`<td>${emi.sno}</td>`);
      printWindow.document.write(`<td>${emi.bookingId}</td>`);
      printWindow.document.write(`<td>${emi.customerName}</td>`);
      printWindow.document.write(`<td>${emi.plotNo}</td>`);
      printWindow.document.write(`<td>${emi.emiNo}</td>`);
      printWindow.document.write(`<td>${emi.dueDate}</td>`);
      printWindow.document.write(`<td>₹${emi.emiAmount.toLocaleString('en-IN')}</td>`);
      printWindow.document.write(`<td>₹${emi.paidAmount.toLocaleString('en-IN')}</td>`);
      printWindow.document.write(`<td>${emi.paidDate}</td>`);
      printWindow.document.write(`<td>${emi.status}</td>`);
      printWindow.document.write('</tr>');
    });

    printWindow.document.write('</tbody></table></body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Filtering logic
  const getFilteredData = () => {
    return sampleData.filter(emi => {
      const matchesSearch = !filters.searchTerm ||
        emi.bookingId.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        emi.customerName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        emi.plotNo.toLowerCase().includes(filters.searchTerm.toLowerCase());

      const matchesStatus = !filters.status || emi.status === filters.status;

      const matchesFromDate = !filters.fromDate || new Date(emi.dueDate) >= new Date(filters.fromDate);
      const matchesToDate = !filters.toDate || new Date(emi.dueDate) <= new Date(filters.toDate);

      return matchesSearch && matchesStatus && matchesFromDate && matchesToDate;
    });
  };

  const handleClearSearch = () => {
    setFilters({ ...filters, searchTerm: '' });
    setPage(0);
  };

  const filteredData = getFilteredData();
  const totalEMI = filteredData.reduce((sum, item) => sum + item.emiAmount, 0);
  const totalPaid = filteredData.reduce((sum, item) => sum + item.paidAmount, 0);
  const totalDue = totalEMI - totalPaid;
  const paidEMIs = filteredData.filter(item => item.status === 'Paid').length;
  const pendingEMIs = filteredData.filter(item => item.status === 'Pending').length;
  const overdueEMIs = filteredData.filter(item => item.status === 'Overdue').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CreditCardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            EMI Report
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Export Data
          </Button>
          {/* <Button variant="outlined" startIcon={<Refresh />}>Refresh</Button> */}
        </Box>
      </Box>

      {/* Search and Filters Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <SearchIcon color="primary" />
          Search & Export EMI Data
        </Typography>

        <Grid container spacing={3}>
          {/* Date Range Filters */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={filters.fromDate}
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                label="Status Filter"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="Paid">Paid EMIs</MenuItem>
                <MenuItem value="Pending">Pending EMIs</MenuItem>
                <MenuItem value="Overdue">Overdue EMIs</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Search Input */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Booking ID, Customer Name, or Plot Number..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: filters.searchTerm && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={handleClearSearch} sx={{ minWidth: 'auto', p: 0.5 }}>
                      <ClearIcon fontSize="small" />
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>

        {/* Search Results Summary */}
        {(filters.searchTerm || filters.fromDate || filters.toDate || filters.status) && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon fontSize="small" />
              <strong>Search Results:</strong> {filteredData.length} EMI{filteredData.length !== 1 ? 's' : ''} found
              {filters.searchTerm && ` for "${filters.searchTerm}"`}
              {filters.status && ` with status "${filters.status}"`}
              {filters.fromDate && ` from ${filters.fromDate}`}
              {filters.toDate && ` to ${filters.toDate}`}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                <TableCell><strong>S.No</strong></TableCell>
                <TableCell><strong>Booking ID</strong></TableCell>
                <TableCell><strong>Customer Name</strong></TableCell>
                <TableCell><strong>Plot No</strong></TableCell>
                <TableCell><strong>EMI No</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>EMI Amount</strong></TableCell>
                <TableCell><strong>Paid Amount</strong></TableCell>
                <TableCell><strong>Paid Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No EMI records found matching your search criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={row.sno} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{row.bookingId}</TableCell>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell>{row.plotNo}</TableCell>
                    <TableCell>{row.emiNo}</TableCell>
                    <TableCell>{row.dueDate}</TableCell>
                    <TableCell>₹{row.emiAmount.toLocaleString('en-IN')}</TableCell>
                    <TableCell>₹{row.paidAmount.toLocaleString('en-IN')}</TableCell>
                    <TableCell>{row.paidDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={row.status === 'Paid' ? 'success' : row.status === 'Pending' ? 'warning' : 'error'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Export Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          Export as PDF
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <Description fontSize="small" />
          </ListItemIcon>
          Export as CSV
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EMIReport;

